/**
 * Session Manager - Handles session storage
 * Uses sessionStorage to clear when tab closes
 * Server validates inactivity (15 minutes)
 */

const SESSION_KEY = 'microvista_session';

export const SessionManager = {
  /**
   * Create a new session
   */
  createSession(token, user) {
    const session = {
      token,
      user,
      createdAt: Date.now(),
      lastActivity: Date.now()
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    this.startActivityTracking();
  },

  /**
   * Get current session
   */
  getSession() {
    const sessionData = sessionStorage.getItem(SESSION_KEY);
    if (!sessionData) return null;

    try {
      return JSON.parse(sessionData);
    } catch (error) {
      // console.error('Error parsing session data:', error);
      this.clearSession();
      return null;
    }
  },

  /**
   * Update last activity timestamp
   */
  updateActivity() {
    const session = this.getSession();
    if (!session) return;

    session.lastActivity = Date.now();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  },

  /**
   * Check if session exists (server validates inactivity)
   */
  isSessionValid() {
    const session = this.getSession();
    return session !== null;
  },

  /**
   * Get inactivity time in milliseconds
   */
  getInactivityTime() {
    const session = this.getSession();
    if (!session) return null;

    return Date.now() - session.lastActivity;
  },

  /**
   * Start tracking user activity
   */
  startActivityTracking() {
    // Track various user activities
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle activity updates to avoid excessive writes
    let lastUpdate = 0;
    const throttleMs = 5000; // Update at most once every 5 seconds

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > throttleMs) {
        this.updateActivity();
        lastUpdate = now;
      }
    };

    // Remove existing listeners first to avoid duplicates
    activityEvents.forEach(event => {
      document.removeEventListener(event, handleActivity);
    });

    // Add activity listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Update activity on page visibility change (when user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        this.updateActivity();
      }
    };
    
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  },

  /**
   * Clear session
   */
  clearSession() {
    sessionStorage.removeItem(SESSION_KEY);
  },

  /**
   * Get token from session
   */
  getToken() {
    const session = this.getSession();
    return session?.token || null;
  },

  /**
   * Get user from session
   */
  getUser() {
    const session = this.getSession();
    return session?.user || null;
  },

  /**
   * Get user role from session
   */
  getUserRole() {
    const session = this.getSession();
    return session?.user?.role || null;
  }
};

